import React, { useEffect, useState, useMemo } from "react";
import { Container, Row, Col, Card, CardBody, Spinner, Input, Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { genericGet, genericPost } from "../../services/Api";
import { ENDPOINTS, SUCCESS_RESULT } from "../../Constants";
import cogoToast from "cogo-toast";
import * as XLSX from "xlsx";

const StaffIDCards = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [cards, setCards] = useState([]);
  const [search, setSearch] = useState("");
  const [staffToReset, setStaffToReset] = useState(null);
  const [resetReason, setResetReason] = useState("");
  const [showResetModal, setShowResetModal] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [viewStaff, setViewStaff] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);

  const loadCards = () => {
    setIsLoading(true);
    return genericGet(ENDPOINTS.STAFF_ID_CARDS)
      .then((res) => {
        setIsLoading(false);
        if (res.status === 200 && res.data.status === SUCCESS_RESULT) {
          setCards(res.data.data || []);
        } else {
          cogoToast.error(res.data?.message || "Failed to load staff ID cards.", {
            hideAfter: 5,
          });
        }
        return res;
      })
      .catch((err) => {
        setIsLoading(false);
        console.error(err);
        cogoToast.error("Something went wrong while loading staff ID cards.", {
          hideAfter: 5,
        });
      });
  };

  useEffect(() => {
    loadCards();
  }, []);

  const filteredCards = useMemo(() => {
    if (!search) return cards;
    const term = search.toLowerCase();
    return cards.filter((c) => {
      return (
        (c.staff_id && String(c.staff_id).toLowerCase().includes(term)) ||
        (c.name && c.name.toLowerCase().includes(term)) ||
        (c.email && c.email.toLowerCase().includes(term)) ||
        (c.office && c.office.toLowerCase().includes(term))
      );
    });
  }, [cards, search]);

  const handleOpenResetModal = (staff) => {
    setStaffToReset(staff);
    setResetReason("");
    setShowResetModal(true);
  };

  const handleResetLinks = () => {
    if (!staffToReset || !staffToReset.id) return;
    if (!resetReason || resetReason.trim().length < 5) {
      cogoToast.error("Please provide a brief reason (at least 5 characters).", {
        hideAfter: 3,
      });
      return;
    }

    setIsResetting(true);
    genericPost(ENDPOINTS.STAFF_ID_CARDS_RESET_LINKS, {
      staff_ids: [staffToReset.id],
      reason: resetReason.trim(),
    })
      .then((res) => {
        setIsResetting(false);
        if (res.status === 200 && res.data.status === SUCCESS_RESULT) {
          cogoToast.success(res.data.message || "Link reset successfully.", {
            hideAfter: 5,
          });
          setShowResetModal(false);
          // Reload cards so we see the updated link
          loadCards().then((reloadRes) => {
            if (
              reloadRes &&
              reloadRes.status === 200 &&
              reloadRes.data.status === SUCCESS_RESULT
            ) {
              const updatedList = reloadRes.data.data || [];
              const updatedStaff = updatedList.find((c) => c.id === staffToReset.id);
              if (updatedStaff) {
                // If the view dialog is open, update it with the latest data
                setViewStaff(updatedStaff);
                setStaffToReset(updatedStaff);
              } else {
                setStaffToReset(null);
              }
            }
          });
        } else {
          cogoToast.error(res.data?.message || "Failed to reset links.", {
            hideAfter: 5,
          });
        }
      })
      .catch((err) => {
        setIsResetting(false);
        console.error(err);
        cogoToast.error("Something went wrong while resetting links.", {
          hideAfter: 5,
        });
      });
  };

  const handleViewStaff = (staff) => {
    setViewStaff(staff);
    setShowViewModal(true);
  };

  const handleCopy = async (label, value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      cogoToast.success(`${label} copied to clipboard`, { hideAfter: 2 });
    } catch (e) {
      console.error("Clipboard error", e);
      cogoToast.error("Failed to copy to clipboard", { hideAfter: 3 });
    }
  };

  const formatCardUrl = (card) => {
    const link = card?.link_address;
    if (!link) return "{link is null}";
    return `ebms.islamicreliefsomalia.org/staff/${link}`;
  };

  const handleExportExcel = () => {
    try {
      const rows = filteredCards.map((c) => ({
        "Staff ID": c?.staff_id || "",
        "img_link": "",
        "Staff Name": c?.staff_name || c?.name || "",
        "Title": c?.designation || "",
        "Branch": c?.office || "",
        "Department": c?.department || "",
        "Blood Group": c?.blood_group || "",
        "link_address": formatCardUrl(c),
      }));

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Staff ID Cards");
      XLSX.writeFile(workbook, "staff_id_cards.xlsx");
    } catch (err) {
      console.error(err);
      cogoToast.error("Failed to export Excel.", { hideAfter: 4 });
    }
  };

  return (
    <Container fluid className="mt-4">
      <Row className="mb-3">
        <Col md="8">
          <h2 className="page-title">Staff ID Cards</h2>
          <p className="text-muted mb-0">
            View generated staff ID cards and their basic details.
          </p>
        </Col>
        <Col md="4" className="text-md-end mt-2 mt-md-0">
          <div className="d-inline-flex gap-2">
            <Button
              color="success"
              size="sm"
              onClick={handleExportExcel}
              disabled={isLoading || filteredCards.length === 0}
              className="me-2"
            >
              Export to Excel
            </Button>
            <Button
              color="primary"
              size="sm"
              onClick={loadCards}
              disabled={isLoading}
            >
              {isLoading ? <Spinner size="sm" /> : "Refresh"}
            </Button>
          </div>
        </Col>
      </Row>

      <Card>
        <CardBody>
          <Row className="mb-3">
            <Col md="4">
              <label className="form-label" style={{ fontSize: "0.85rem" }}>
                Search (name, staff ID, email, office)
              </label>
              <Input
                bsSize="sm"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Type to filter..."
              />
            </Col>
          </Row>

          {isLoading ? (
            <div className="text-center py-4">
              <Spinner />
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-striped table-hover table-sm mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: "50px" }}>#</th>
                    <th>Staff ID</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Office</th>
                    <th>Designation</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCards.length === 0 && (
                    <tr>
                      <td colSpan={8} className="text-center py-3">
                        No staff ID cards found.
                      </td>
                    </tr>
                  )}
                  {filteredCards.map((card, index) => (
                    <tr key={card.id || index}>
                      <td>{index + 1}</td>
                      <td>{card.staff_id}</td>
                      <td>{card.staff_name || card.name}</td>
                      <td>{card.email}</td>
                      <td>{card.office}</td>
                      <td>{card.designation}</td>
                      <td>
                        <Button
                          color="secondary"
                          size="sm"
                          onClick={() => handleViewStaff(card)}
                        >
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* View staff details modal */}
      <Modal isOpen={showViewModal} toggle={() => setShowViewModal(false)}>
        <ModalHeader toggle={() => setShowViewModal(false)}>
          Staff ID Card Details
        </ModalHeader>
        <ModalBody>
          {viewStaff ? (
            <div>
              {[
                { label: "Staff ID", key: "staff_id" },
                { label: "Name", key: "staff_name", fallbackKey: "name" },
                { label: "Office", key: "office" },
                { label: "Designation", key: "designation" },
                { label: "Department", key: "department" },
                { label: "Blood Group", key: "blood_group" },
                {
                  label: "Card URL",
                  key: "card_url",
                  formatter: (staff) => {
                    const link = staff.link_address;
                    if (!link) return "{link is null}";
                    return `ebms.islamicreliefsomalia.org/staff/${link}`;
                  },
                },
              ].map((field) => {
                const rawValue =
                  (field.formatter && field.formatter(viewStaff)) ||
                  viewStaff[field.key] ||
                  (field.fallbackKey ? viewStaff[field.fallbackKey] : "");
                const value = rawValue || "";
                return (
                  <div
                    key={field.label}
                    className="d-flex justify-content-between align-items-center mb-2"
                  >
                    <div>
                      <strong>{field.label}:</strong>{" "}
                      <span style={{ wordBreak: "break-all" }}>
                        {value || "-"}
                      </span>
                    </div>
                    {value && (
                      <Button
                        color="link"
                        size="sm"
                        onClick={() => handleCopy(field.label, value)}
                      >
                        <i className="fa fa-copy" />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p>No staff selected.</p>
          )}
        </ModalBody>
        <ModalFooter>
          {viewStaff && (
            <Button
              color="danger"
              onClick={() => handleOpenResetModal(viewStaff)}
              disabled={isResetting}
            >
              Reset Link
            </Button>
          )}
          <Button
            color="secondary"
            onClick={() => setShowViewModal(false)}
            disabled={isResetting}
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Reset link confirmation modal (per staff) */}
      <Modal isOpen={showResetModal} toggle={() => setShowResetModal(false)}>
        <ModalHeader toggle={() => setShowResetModal(false)}>
          Reset Staff ID Card Links
        </ModalHeader>
        <ModalBody>
          <p>
            You are about to reset the <strong>link_address</strong> for:
          </p>
          {staffToReset && (
            <ul>
              <li>
                {staffToReset.staff_name || staffToReset.name} (
                {staffToReset.staff_id})
              </li>
            </ul>
          )}
          <div className="mt-3">
            <label className="form-label" style={{ fontSize: "0.85rem" }}>
              Reason for reset
            </label>
            <Input
              type="textarea"
              rows={3}
              bsSize="sm"
              value={resetReason}
              onChange={(e) => setResetReason(e.target.value)}
              placeholder="Describe why you are resetting this staff ID card link..."
            />
          </div>
          <p className="text-danger mt-2 mb-0">
            Are you sure you want to proceed?
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setShowResetModal(false)}
            disabled={isResetting}
          >
            Cancel
          </Button>
          <Button
            color="danger"
            onClick={handleResetLinks}
            disabled={isResetting}
          >
            {isResetting ? <Spinner size="sm" /> : "Confirm Reset"}
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

export default StaffIDCards;

